import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TeranetService } from '@services/teranet/teranet.service';
import { TeranetDocket } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterActions } from '@state/matter';
import LogRocket from 'logrocket';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { uniqBy } from 'lodash';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { TeranetRepoService } from '@repos/teranet/teranet-repo.service';
import { TestHooks } from './docket-create.hooks';
import { DocketSearchDetail, TeranetCredentials } from '@models/teranet';

enum ButtonTab {
	CREATE = 'create',
	SEARCH = 'search',
}

@UntilDestroy()
@Component({
	selector: 'app-teranet-docket-create',
	templateUrl: './docket-create.component.html',
	styleUrls: ['./docket-create.component.scss'],
	standalone: false,
})
export class TeranetDocketCreateComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(SelectedMatter.id) matterId;
	@Select(SelectedMatter.matterName) matterName$: Observable<string>;
	@Select(SelectedMatter.matterNumber) matterNumber$: Observable<string>;

	@SelectSnapshot(SelectedMatter.getTeranetAccountCredentials)
	teranetAccount: TeranetCredentials;
	@Select(SelectedMatter.teranetDocket)
	teranetDocket$: Observable<TeranetDocket>;

	public creating = false;
	public docketName: string;
	public docketId: string;
	public error: string;
	public submitted = false;
	public tabs = ButtonTab;
	public ICON = ICON;
	public assigning = false;

	public docketSearchId: string;
	public submittedSearchId = false;

	public docketSearchValue: string;
	public submittedSearch = false;
	public searchResults: DocketSearchDetail[] = null;
	public selectedDocket: DocketSearchDetail = null;

	public tabOptions: ButtonGroupOption<ButtonTab>[] = [
		{ label: 'Create', value: ButtonTab.CREATE },
		{ label: 'Search', value: ButtonTab.SEARCH },
	];

	public selectedTab = ButtonTab.CREATE;
	public searching = false;

	constructor(
		private teranetService: TeranetService,
		private routeService: NavigationService,
		private snackbarService: SnackbarService,
		private teranetRepo: TeranetRepoService,
	) {}

	ngOnInit(): void {
		combineLatest([this.matterNumber$, this.matterName$])
			.pipe(
				untilDestroyed(this),
				take(1),
				map(([number, name]) => {
					this.docketId = number;
					this.docketName = name;
				}),
			)
			.subscribe();

		// Anfernee: I believe this is messing up the routing not removing it in case I'm wrong
		// this.teranetDocket$.pipe(untilDestroyed(this)).subscribe((value) => {
		// 	if (value?.docketId) {
		// 		this.routeService.goToMatter_TitleSearch(this.matterId);
		// 	}
		// });
	}

	@Dispatch()
	SetDocket(
		matterId: string,
		docketId: string,
		docketName: string,
		docketSystemId: string,
		accountname: string,
		username: string,
	): MatterActions.SetDocket {
		return new MatterActions.SetDocket({
			matterId,
			docket: {
				docketName,
				docketId,
				docketTeranetSystemId: docketSystemId,
				accountname,
				username,
			},
		});
	}

	setSelectedTab(tab: ButtonTab) {
		if (tab === ButtonTab.SEARCH) {
			this.docketSearchValue = this.docketId ?? this.docketName;
		}
		this.selectedTab = tab;
	}

	async createDocket(): Promise<void> {
		try {
			this.creating = true;
			this.error = '';

			const result = await firstValueFrom(
				this.teranetService.createDocket({
					docket_name: this.docketName,
					docket_id: this.docketId,
				}),
			);
			this.SetDocket(
				this.matterId,
				this.docketId,
				this.docketName,
				result.docket_system_id,
				this.teranetAccount.accountName,
				this.teranetAccount.username,
			);
		} catch (err) {
			const friendlyError = this.teranetService.HandleError(
				'Unable to create docket',
				err,
			);
			this.error = friendlyError;
			LogRocket.error('Unable to create docket', err);
		} finally {
			this.creating = false;
		}
	}

	onSubmit(form: NgForm): Promise<void> {
		this.submitted = true;
		this.error = null;
		if (form.valid) {
			return this.createDocket();
		}
	}

	searchDocket(form: NgForm) {
		if (form.valid) {
			this.searching = true;
			this.selectedDocket = null;

			const byName = this.teranetService
				.searchDocketByName({
					docket_name: this.docketSearchValue,
				})
				.pipe(
					catchError(() =>
						of({
							docket_search_details: [],
						}),
					),
				);

			const byId = this.teranetService
				.searchDocketById({
					docket_id: this.docketSearchValue,
				})
				.pipe(
					catchError(() =>
						of({
							docket_search_details: [],
						}),
					),
				);

			combineLatest([byName, byId])
				.pipe(untilDestroyed(this))
				.subscribe(([resultByName, resultById]) => {
					if (
						resultByName?.docket_search_details &&
						resultById?.docket_search_details
					) {
						const combined = [
							...resultByName.docket_search_details,
							...resultById.docket_search_details,
						];
						this.searchResults = uniqBy(combined, 'docket_id');
						this.searching = false;
						if (this.searchResults.length === 1) {
							this.selectedDocket = this.searchResults[0];
						}
					}
				});
		}
	}

	trackByDocketId(index: number, item: DocketSearchDetail) {
		return item?.docket_id;
	}

	selectDocket(docket: DocketSearchDetail) {
		this.selectedDocket = docket;
	}

	async assignDocket() {
		if (this.selectedDocket) {
			this.assigning = true;
			try {
				await this.teranetRepo.AssignDocket(this.matterId, this.selectedDocket);
			} catch (err) {
				this.snackbarService.openSnackBar({
					text: 'There was an unknown issue while attempting to assign the docket',
					type: 'error',
				});
			} finally {
				this.assigning = false;
			}
		}
	}
}
